import {
    IonPage, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton,
    IonContent, IonList, IonItem, IonLabel, IonDatetime, IonModal,
    IonToast, IonSpinner, IonNote
} from '@ionic/react';
import { useState, useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { userProfileState } from '../services/state.service';
import { getSalesActivitiesForShare } from '../services/sales.service';
import { getUsersWithRolesSupport } from '../services/user.service';
import SelectItem from './SelectItem';

interface IProps {
    onClose: () => void;
}

const ShareActivitiesModal: React.FC<IProps> = ({ onClose }) => {
    const userProfile: any = useRecoilValue(userProfileState);

    const [selectedDate, setSelectedDate] = useState(new Date().toISOString());
    const [servicedBy, setServicedBy] = useState(userProfile.username);
    const [servicedByName, setServicedByName] = useState(userProfile.name || userProfile.username);
    const [supportUsers, setSupportUsers] = useState<any[]>([]);
    const [showUserModal, setShowUserModal] = useState(false);
    const [toast, setToast] = useState('');

    const [isPreparing, setIsPreparing] = useState(false);
    const [isSharing, setIsSharing] = useState(false);
    const [hasShared, setHasShared] = useState(false);
    const [preparedText, setPreparedText] = useState<string | null>(null);
    const [preparedFiles, setPreparedFiles] = useState<File[]>([]);
    const [prepareStatus, setPrepareStatus] = useState('');
    const prepareRef = useRef(0);

    useEffect(() => {
        getUsersWithRolesSupport().then(users => setSupportUsers(users));
    }, []);

    useEffect(() => {
        prepareShareData();
    }, [selectedDate, servicedBy]); // eslint-disable-line

    const formatTime = (dateStr: string) =>
        new Intl.DateTimeFormat('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })
            .format(new Date(dateStr));

    const formatDateDisplay = (dateStr: string) =>
        new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
            .format(new Date(dateStr));

    const buildShareText = (activities: any[], name: string, date: string) => {
        const lines: string[] = [
            `📊 *Activities - ${formatDateDisplay(date)}*`,
            `👤 ${name}`,
            `━━━━━━━━━━━━`,
            ''
        ];
        activities.forEach((act, idx) => {
            const type = act.salesActivityType?.description || 'Activity';
            const group = act.group
                ? ` [${act.group.charAt(0).toUpperCase() + act.group.slice(1)}]`
                : '';
            lines.push(`${idx + 1}. *${type}*${group}`);
            lines.push(`   📅 ${formatTime(act.date)}`);
            if (act.place) {
                const placeParts = act.place.split(',').map((p: string) => p.trim());
                const unique = placeParts.filter((p: string, i: number) => p.toLowerCase() !== placeParts[i - 1]?.toLowerCase());
                lines.push(`   📍 ${unique.slice(0, 2).join(', ')}`);
            }
            if (act.contact) lines.push(`   📞 ${act.contact}`);
            if (act.userIds?.length > 0)
                lines.push(`   👥 ${act.userIds.map((u: any) => u.name).join(', ')}`);
            if (act.comment) lines.push(`   💬 ${act.comment}`);
            lines.push('');
        });
        lines.push(`━━━━━━━━━━━━`);
        lines.push(`Total: ${activities.length} ${activities.length === 1 ? 'activity' : 'activities'}`);
        return lines.join('\n');
    };

    const prepareShareData = async () => {
        const token = ++prepareRef.current;
        setIsPreparing(true);
        setPreparedText(null);
        setPreparedFiles([]);
        setPrepareStatus('');
        setHasShared(false);

        try {
            const activities = await getSalesActivitiesForShare(servicedBy, new Date(selectedDate));
            if (token !== prepareRef.current) return;

            if (!activities || activities.length === 0) {
                setPrepareStatus('No activities found for this date and user');
                return;
            }

            const text = buildShareText(activities, servicedByName, selectedDate);
            setPreparedText(text);

            const totalImages = activities.reduce((sum: number, act: any) =>
                sum + (act.attachments?.images?.length || 0), 0);
            setPrepareStatus(`${activities.length} ${activities.length === 1 ? 'activity' : 'activities'}${totalImages > 0 ? `, ${totalImages} image(s)` : ''} ready`);

            // Fetch image files in background for platforms that support file sharing (HTTPS)
            if (totalImages > 0 && window.isSecureContext) {
                try {
                    const baseUrl = process.env.REACT_APP_CORE_API_BASEPATH;
                    const imageUrls: string[] = [];
                    activities.forEach((act: any) => {
                        if (act.attachments?.images?.length > 0)
                            imageUrls.push(...act.attachments.images);
                    });
                    const files = await Promise.all(
                        imageUrls.map(async (url, i) => {
                            const s3Key = url.replace(/^https?:\/\/[^/]+\//, '');
                            const proxyUrl = `${baseUrl}/images?key=${encodeURIComponent(s3Key)}`;
                            const authToken = localStorage.getItem('access_token') ?? '';
                            const res: Response = await fetch(proxyUrl, {
                                headers: { Authorization: `Bearer ${authToken}` }
                            });
                            const blob: Blob = await res.blob();
                            const ext = s3Key.split('.').pop()?.toLowerCase() === 'png' ? 'png' : 'jpg';
                            return new File([blob], `activity_${i + 1}.${ext}`, {
                                type: ext === 'png' ? 'image/png' : 'image/jpeg'
                            });
                        })
                    );
                    if (token !== prepareRef.current) return;
                    setPreparedFiles(files);
                } catch {
                    // HTTPS but fetch failed — text with URLs is the fallback
                }
            }
        } catch {
            if (token !== prepareRef.current) return;
            setPrepareStatus('Failed to load activities');
        } finally {
            if (token === prepareRef.current) setIsPreparing(false);
        }
    };

    const handleUserSelect = (item: any) => {
        setServicedBy(item.farmUserId);
        setServicedByName(item.username || item.farmUserId);
        setShowUserModal(false);
    };

    const handleShare = () => {
        if (!preparedText || isSharing || hasShared) return;

        const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
        const hasFiles = preparedFiles.length > 0 &&
            (!((navigator as any).canShare) || (navigator as any).canShare({ files: preparedFiles }) !== false);

        if (isIOS && navigator.share) {
            // iOS: native share sheet handles text + files in one shot — no text-dropping issue.
            const shareData: any = hasFiles ? { files: preparedFiles, text: preparedText } : { text: preparedText };
            navigator.share(shareData)
                .then(() => setHasShared(true))
                .catch((err: any) => {
                    if (err?.name !== 'AbortError') {
                        // Files may have caused the error — retry with text only.
                        navigator.share({ text: preparedText })
                            .then(() => setHasShared(true))
                            .catch(() => setToast('Failed to share. Please try again.'));
                    }
                });
        } else {
            // Android/Desktop: navigator.share({ text }) silently drops text on many Android OEMs,
            // so use wa.me to reliably pre-fill WhatsApp text.
            // Images are exposed via a persistent button after this — NOT an IonAlert, because
            // window.open immediately backgrounds the app on Android causing any alert to be
            // dismissed before the user can interact with it.
            window.open(`https://wa.me/?text=${encodeURIComponent(preparedText)}`, '_blank');
            setHasShared(true);
        }
    };

    const handleShareImages = () => {
        if (preparedFiles.length === 0 || !navigator.share || isSharing) return;
        setIsSharing(true);
        navigator.share({ files: preparedFiles } as any)
            .catch((err: any) => {
                if (err?.name !== 'AbortError') setToast('Failed to share images.');
            })
            .finally(() => setIsSharing(false));
    };

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="end">
                        <IonButton onClick={onClose}>Close</IonButton>
                    </IonButtons>
                    <IonTitle>Share Activities</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent>
                <IonList>
                    <IonItem>
                        <IonLabel position="stacked">Date</IonLabel>
                        <IonDatetime
                            displayFormat="DD/MM/YYYY"
                            value={selectedDate}
                            onIonChange={e => setSelectedDate(e.detail.value!)}
                        />
                    </IonItem>
                    <IonItem button onClick={() => setShowUserModal(true)}>
                        <IonLabel position="stacked">Serviced By</IonLabel>
                        <p style={{ margin: '8px 0', fontSize: '16px' }}>{servicedByName}</p>
                    </IonItem>
                </IonList>

                {prepareStatus ? (
                    <IonNote style={{ display: 'block', padding: '8px 16px', fontSize: '13px' }}>
                        {prepareStatus}
                    </IonNote>
                ) : null}

                <div style={{ padding: '16px' }}>
                    <IonButton
                        expand="block"
                        disabled={isPreparing || !preparedText || isSharing || hasShared}
                        onClick={handleShare}
                        color="success"
                    >
                        {isPreparing ? (
                            <><IonSpinner name="crescent" style={{ marginRight: 8 }} /> Preparing...</>
                        ) : isSharing ? (
                            <><IonSpinner name="crescent" style={{ marginRight: 8 }} /> Opening WhatsApp...</>
                        ) : hasShared ? (
                            '✅ Text Shared'
                        ) : '📤 Share via WhatsApp'}
                    </IonButton>

                    {hasShared && preparedFiles.length > 0 &&
                        !!navigator.share &&
                        (!!(navigator as any).canShare?.({ files: preparedFiles })) && (
                        <IonButton
                            expand="block"
                            style={{ marginTop: 12 }}
                            disabled={isSharing}
                            onClick={handleShareImages}
                            color="primary"
                        >
                            {isSharing ? (
                                <><IonSpinner name="crescent" style={{ marginRight: 8 }} /> Sharing...</>
                            ) : `📷 Share ${preparedFiles.length} Image${preparedFiles.length === 1 ? '' : 's'}`}
                        </IonButton>
                    )}
                </div>

                {showUserModal && (
                    <IonModal isOpen={showUserModal}>
                        <SelectItem
                            label="Select User"
                            items={supportUsers}
                            labelKey="username"
                            valueKey="farmUserId"
                            selectedValue={servicedBy}
                            setItem={handleUserSelect}
                            onClose={() => setShowUserModal(false)}
                        />
                    </IonModal>
                )}

                <IonToast
                    isOpen={!!toast}
                    onDidDismiss={() => setToast('')}
                    message={toast}
                    duration={3000}
                    color="warning"
                />
            </IonContent>
        </IonPage>
    );
};

export default ShareActivitiesModal;
